/**
 * The Bullseye's side column, minus the boundaries panel: the AI Review lists
 * (each switchable for this session) and the legend.
 */
import type { BullseyeList } from "@nu-assist/contracts";
import type { ReactNode } from "react";

import { type RingModel, ringTone } from "./bullseyeGeometry";
import { listSubtitle, runBy } from "./bullseyeFormat";
import type { PlottedList } from "./BullseyeTarget";
import { RING_BG } from "./bullseyeTones";

const PANEL = "flex flex-col gap-2.5 rounded-md border border-border bg-surface p-3.5 shadow-sm";
const PANEL_TITLE = "text-caption font-semibold uppercase tracking-wider text-text-secondary";

export interface ListsPanelProps {
  /** Every list of the search, on or off, in the search's order. */
  lists: readonly PlottedList[];
  isOn: (listId: string) => boolean;
  onToggle: (listId: string, on: boolean) => void;
  /** People of `list` who land in a ring under the current rule. */
  plotted: (list: BullseyeList) => number;
  present: boolean;
}

export function ListsPanel({ lists, isOn, onToggle, plotted, present }: ListsPanelProps) {
  return (
    <section className={PANEL} data-testid="bullseye-lists">
      <h3 className={PANEL_TITLE}>AI Review lists</h3>
      <div className="-mx-1.5 flex flex-col gap-0.5">
        {lists.map(({ list, title, tone }) => {
          const on = isOn(list.ai_review_id);
          return (
            <label
              key={list.ai_review_id}
              className="grid cursor-pointer grid-cols-[auto_auto_1fr_auto] items-center gap-2.5 rounded-sm px-1.5 py-2 hover:bg-row-hover"
              data-testid="bullseye-list-row"
              data-list={list.ai_review_id}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={(e) => onToggle(list.ai_review_id, e.target.checked)}
                className="h-4 w-4 accent-accent"
                aria-label={`Show ${title}`}
              />
              <span
                className={`inline-block h-2.5 w-2.5 rounded-full ring-1 ring-bg light:ring-text ${tone.bg} ${
                  on ? "" : "opacity-30"
                }`}
              />
              <span className="min-w-0 text-label font-medium leading-snug text-text">
                {title}
                <span className="block text-caption font-normal text-text-muted">
                  {listSubtitle(list)}
                </span>
                {!present && (
                  <span
                    className="block text-caption font-normal text-text-secondary"
                    data-testid="bullseye-run-by"
                  >
                    {runBy(list)}
                  </span>
                )}
              </span>
              <span className="text-right text-label font-semibold tabular-nums text-text">
                {plotted(list)}
                <small className="block text-caption font-normal text-text-muted">
                  of {list.people.length}
                </small>
              </span>
            </label>
          );
        })}
      </div>
      {!present && (
        <p className="text-caption text-text-muted">Lists without an AI Review can't be plotted.</p>
      )}
    </section>
  );
}

export interface LegendProps {
  model: RingModel;
  /** People per ring across the active lists, centre first. */
  ringCounts: readonly number[];
  nonfitTotal: number;
}

export function Legend({ model, ringCounts, nonfitTotal }: LegendProps) {
  const rows: ReactNode[] = [];
  for (let b = 0; b < model.count; b++) {
    rows.push(
      <LegendRow
        key={b}
        swatch={<span className={`h-4 w-4 rounded-full ${RING_BG[ringTone(b, model.count)]}`} />}
        title={model.label(b)}
        desc={model.desc(b)}
        count={ringCounts[b] ?? 0}
      />
    );
  }
  return (
    <section className={PANEL} data-testid="bullseye-legend">
      <h3 className={PANEL_TITLE}>
        {model.mode === "criteria_met" ? "Must-haves met" : "AI score"}
      </h3>
      <div className="flex flex-col divide-y divide-border">
        {rows}
        <LegendRow
          muted
          swatch={
            <span className="h-4 w-4 rounded-full border-2 border-dashed border-text-muted" />
          }
          title="Not a fit"
          desc={model.nonfitDesc}
          count={nonfitTotal}
        />
      </div>
    </section>
  );
}

function LegendRow({
  swatch,
  title,
  desc,
  count,
  muted = false,
}: {
  swatch: ReactNode;
  title: string;
  desc: string;
  count: number;
  muted?: boolean;
}) {
  return (
    <div
      className="grid grid-cols-[auto_1fr_auto] items-center gap-2.5 py-2"
      data-testid="bullseye-legend-row"
    >
      {swatch}
      <span
        className={`text-label font-medium leading-tight ${muted ? "text-text-secondary" : "text-text"}`}
      >
        {title}
        <small className="block text-caption font-normal text-text-muted">{desc}</small>
      </span>
      <span
        className={`text-h2 font-bold tabular-nums ${muted ? "text-text-secondary" : "text-text"}`}
      >
        {count}
      </span>
    </div>
  );
}
